import type { NotificationCategory, NotificationVariables } from '../notification-dto';

export type DataFor<C extends NotificationCategory> = Extract<
  NotificationVariables,
  { category: C }
>['data'];

export type ServerCopyTranslate = (key: string, values?: Record<string, unknown>) => string;

export interface ServerCopyDescriptor<C extends NotificationCategory> {
  titleKey: string | ((data: DataFor<C>) => string);
  bodyKey: string | ((data: DataFor<C>) => string);
  values: (data: DataFor<C>, tn: ServerCopyTranslate) => Record<string, unknown>;
}

export interface CategoryDescriptor<C extends NotificationCategory> {
  category: C;
  isAvailable: (data: DataFor<C>) => boolean;
  actionUrl: (data: DataFor<C>, organizationId: string) => string;
  serverCopy?: ServerCopyDescriptor<C>;
  pills?: (data: DataFor<C>, ctx: { loopName?: string }) => string[];
}

export const requireFields = <T>(...keys: (keyof T)[]): ((data: T) => boolean) => {
  return (data: T) => keys.every((key) => data[key] != null);
};

export const resolveKey = <T>(key: string | ((data: T) => string), data: T): string =>
  typeof key === 'function' ? key(data) : key;

export const extractRelativeActionUrl = (actionUrl: string): string => {
  for (const prefix of ['/org-id/', '/org/']) {
    const startPos = actionUrl.indexOf(prefix);
    if (startPos !== -1) return actionUrl.substring(startPos);
  }
  try {
    const parsed = new URL(actionUrl);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return actionUrl;
  }
};

export const defineNotificationCategory = <C extends NotificationCategory>(
  category: C,
  descriptor: Omit<CategoryDescriptor<C>, 'category'>,
): CategoryDescriptor<C> => ({ category, ...descriptor });

export const integrationPills = (data: {
  integrationName?: string;
  sourceSystemType?: string;
}): string[] => {
  const pills: string[] = [];
  if (data.integrationName) pills.push(data.integrationName);
  if (data.sourceSystemType) pills.push(data.sourceSystemType);
  return pills.length > 0 ? pills : ['Integration'];
};
